import { gql, useQuery, useMutation } from '@apollo/client'
import { Edit, Delete, LocationOn, Add, Image } from '@mui/icons-material'
import {
  Avatar,
  Button,
  CircularProgress,
  DialogContent,
  Grid,
  IconButton,
  List,
  debounce,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
  MenuItem,
} from '@mui/material'
import {
  Maybe,
  Query,
  RetailerAccount,
  Location,
  Retailer,
} from '@promoboxx/graphql-gateway-types'
import { Formik, Form, Field } from 'formik'
import { TextField } from 'formik-mui'
import I18n from 'i18n-js'
import { enqueueSnackbar, useSnackbar } from 'notistack'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import * as Yup from 'yup'

import acceptedCountries, {
  AcceptedCountry,
} from '@src/components/payment/acceptedCountries'
import GooglePlace from '@src/components/shared/GooglePlace'
import AppConfig from '@src/config/AppConfig'
import ConfirmDialog from '@src/lib/ConfirmDialog'
import LinkChild from '@src/lib/LinkChild'
import { useLocations } from '@src/lib/useLocations'
import { currentRetailerAccountSelector } from '@src/stores/app/selectors'

import GoogleMap from '../shared/GoogleMap'
import MobileFullScreenDialog from '../shared/MobileFullScreenDialog'

const UPDATE_RETAILER_ACCOUNT_BUSINESS_INFO = gql`
  mutation UpdateRetailerAccount(
    $updateRetailerAccountId: ID!
    $params: UpdateRetailerAccountParams!
    $args: UpdateRetailerAccountArgs
  ) {
    updateRetailerAccount(
      id: $updateRetailerAccountId
      params: $params
      args: $args
    ) {
      id
      name
      phone
      phone_extension
      logo_url
      website
      retailers {
        id
        website
      }
    }
  }
`

const GET_RETAILER_ACCOUNT_BUSINESS_INFO = gql`
  query getRetailerAccountInfo($id: ID!) {
    getRetailerAccount(id: $id, fields: ["retailers"]) {
      id
      name
      phone
      phone_extension
      logo_url
      website
      retailers {
        id
        website
      }
    }
  }
`

const businessInfoSchema = Yup.object().shape({
  name: Yup.string()
    .required()
    .label(I18n.t('retailerSettings.businessInfo.name')),
  phone: Yup.string().nullable(),
  phone_extension: Yup.string().nullable(),
  website: Yup.string().nullable(),
})

const businessLogoSchema = Yup.object().shape({
  file: Yup.mixed()
    .required()
    .label(I18n.t('retailerSettings.businessInfo.logo')),
})

const businessLocationSchema = Yup.object().shape({
  addr1: Yup.string()
    .required()
    .label(I18n.t('retailerSettings.businessInfo.addr1')),
  addr2: Yup.string().nullable(),
  city: Yup.string()
    .required()
    .label(I18n.t('retailerSettings.businessInfo.city')),
  state: Yup.string()
    .required()
    .label(I18n.t('retailerSettings.businessInfo.state')),
  country_code: Yup.string()
    .required()
    .label(I18n.t('retailerSettings.businessInfo.country')),
  zip: Yup.string()
    .required()
    .label(I18n.t('retailerSettings.businessInfo.country'))
    .test('is-valid-postal-code', 'Invalid postal code', function (value) {
      const { country_code } = this.parent
      const pattern = acceptedCountries.find(
        (country: AcceptedCountry) => country.code === country_code,
      )?.postalRegex
      if (pattern && value) {
        return pattern.test(value)
      }
      return pattern ? false : true
    }),
  phone: Yup.string().nullable(),
  phone_extension: Yup.string().nullable(),
})

const RetailerSettingsBusinessInfo: React.FC = () => {
  const retailerAccountId = useSelector(currentRetailerAccountSelector)?.id
  const { data, loading, refetch } = useQuery<Query>(
    GET_RETAILER_ACCOUNT_BUSINESS_INFO,
    {
      variables: {
        id: retailerAccountId,
      },
    },
  )

  if (loading) {
    return (
      <Stack spacing={1} alignItems="center" sx={{ marginTop: 20 }}>
        <CircularProgress />
        <Typography variant="h6" textAlign="center">
          {I18n.t('shared.loading')}...
        </Typography>
      </Stack>
    )
  }

  return (
    <Grid container spacing={6}>
      <Grid item xs={12} sm={8} lg={6}>
        <Stack spacing={2}>
          <Typography variant="h6" gutterBottom>
            {I18n.t('retailerSettings.businessInfo.title')}
          </Typography>
          <Paper sx={{ padding: 4 }}>
            <BusinessInfoForm retailerAccount={data?.getRetailerAccount} />
          </Paper>
        </Stack>
      </Grid>
      <Grid item xs={12} sm={4} lg={6}>
        <Stack spacing={2}>
          <Typography variant="h6" gutterBottom>
            {I18n.t('retailerSettings.businessInfo.logoTitle')}
          </Typography>
          <Paper sx={{ padding: 4 }}>
            <BusinessLogoForm
              onComplete={refetch}
              retailerAccount={data?.getRetailerAccount}
            />
          </Paper>
        </Stack>
      </Grid>
      <Grid item xs={12}>
        <Stack spacing={2}>
          <Typography variant="h6" gutterBottom>
            {I18n.t('retailerSettings.businessInfo.location.title')}
          </Typography>
          <Paper>
            <BusinessLocations />
          </Paper>
        </Stack>
      </Grid>
    </Grid>
  )
}

const BusinessInfoForm: React.FC<{
  onComplete?: () => void
  retailerAccount?: Maybe<RetailerAccount>
}> = ({ retailerAccount, onComplete = () => {} }) => {
  const [updateRetailerAccount] = useMutation(
    UPDATE_RETAILER_ACCOUNT_BUSINESS_INFO,
  )
  const { enqueueSnackbar } = useSnackbar()
  return (
    <Formik
      enableReinitialize
      initialValues={{
        name: retailerAccount?.name,
        phone: retailerAccount?.phone,
        phone_extension: retailerAccount?.phone_extension,
        website: retailerAccount?.website,
      }}
      validationSchema={businessInfoSchema}
      onSubmit={async (values, { setSubmitting, resetForm }) => {
        try {
          await updateRetailerAccount({
            variables: {
              updateRetailerAccountId: retailerAccount?.id,
              params: {
                name: values.name,
                phone: values.phone || '',
                phone_extension: values.phone_extension || '',
                website: values.website,
              },
              args: {
                fields: ['retailers'],
              },
            },
          })

          enqueueSnackbar(
            I18n.t(
              'retailerSettings.businessInfo.notifications.updateInfoSuccess',
            ),
            { variant: 'success' },
          )
          resetForm()
          onComplete()
        } catch (err) {
          enqueueSnackbar(
            I18n.t(
              'retailerSettings.businessInfo.notifications.updateInfoError',
            ),
            { variant: 'error' },
          )
          setSubmitting(false)
        }
      }}
    >
      {({ isSubmitting, dirty }) => (
        <Form>
          <Stack spacing={4}>
            <Field
              component={TextField}
              fullWidth
              name="name"
              label={I18n.t('retailerSettings.businessInfo.name')}
            />

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              flexWrap="wrap"
            >
              <Field
                sx={{ flex: 1, minWidth: 150 }}
                component={TextField}
                fullWidth
                name="phone"
                label={I18n.t('retailerSettings.businessInfo.phone')}
              />
              <Field
                sx={{ flex: 0.5, minWidth: 150 }}
                component={TextField}
                fullWidth
                name="phone_extension"
                label={I18n.t('retailerSettings.businessInfo.phoneExtension')}
              />
            </Stack>
            <Field
              component={TextField}
              fullWidth
              name="website"
              label={I18n.t('retailerSettings.businessInfo.website')}
              helperText={I18n.t(
                'retailerSettings.businessInfo.websiteFieldDescription',
              )}
            />

            <Stack direction="row" justifyContent="flex-end">
              <Button
                type="submit"
                disabled={isSubmitting || !dirty}
                variant="contained"
                color="primary"
              >
                {I18n.t('shared.save')}
              </Button>
            </Stack>
          </Stack>
        </Form>
      )}
    </Formik>
  )
}

const BusinessLogoForm: React.FC<{
  onComplete?: () => void
  retailerAccount?: Maybe<RetailerAccount>
}> = ({ retailerAccount, onComplete = () => {} }) => {
  const headers = useSelector((state) => state.auth.headers)
  const debouncedSubmit = debounce((formik) => {
    formik.submitForm()
  }, 10)
  return (
    <Formik
      enableReinitialize
      initialValues={{
        file: undefined,
      }}
      validationSchema={businessLogoSchema}
      onSubmit={async (values, { setSubmitting, resetForm }) => {
        const formData = new FormData()
        // graphql federation doesn't support file uploads so we have to use REST
        if (values.file) {
          formData.append('retailer_account[logo]', values.file)
        }

        const response = await fetch(
          `${AppConfig.API_BASE}/v2/retailer_accounts/${retailerAccount?.id}`,
          {
            method: 'PUT',
            headers,
            body: formData,
          },
        )
        if (response.ok) {
          enqueueSnackbar(
            I18n.t(
              'retailerSettings.businessInfo.notifications.updateLogoSuccess',
            ),
            { variant: 'success' },
          )
          resetForm()
          onComplete()
        } else {
          enqueueSnackbar(
            I18n.t(
              'retailerSettings.businessInfo.notifications.updateLogoError',
            ),
            { variant: 'error' },
          )
          setSubmitting(false)
        }
      }}
    >
      {(formik) => (
        <Form>
          <Stack spacing={2} alignItems="center">
            <Button
              aria-label={I18n.t('retailerSettings.businessInfo.changeLogo')}
              disabled={formik.isSubmitting}
              component="label"
              htmlFor="logo-image-upload"
            >
              <Avatar
                src={retailerAccount?.logo_url || undefined}
                alt={I18n.t('retailerSettings.businessInfo.logo')}
                sx={{ width: 128, height: 128 }} // Fixed 1:1 size
              >
                <Image fontSize="large" />
              </Avatar>
            </Button>
            <Button
              disabled={formik.isSubmitting}
              component="label"
              htmlFor="logo-image-upload"
              endIcon={<Edit />}
            >
              {I18n.t('retailerSettings.businessInfo.changeLogo')}
            </Button>
          </Stack>
          <input
            id="logo-image-upload"
            type="file"
            onChange={(e) => {
              if (e.target.files) {
                formik.setFieldValue('file', e.target.files[0])
                // debouce to prevent handler warning
                debouncedSubmit(formik)
              }
            }}
            hidden
          />
        </Form>
      )}
    </Formik>
  )
}

const BusinessLocations: React.FC<{}> = () => {
  const [formOpen, setFormOpen] = useState(false)
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false)
  const [selectedLocation, setSelectedLocation] = useState<
    Location | undefined
  >(undefined)
  const retailerAccount = useSelector(currentRetailerAccountSelector)
  const { deleteLocations, locations, getLocations } =
    useLocations(retailerAccount)

  const { enqueueSnackbar } = useSnackbar()
  const handleClose = () => {
    setSelectedLocation(undefined)
    setRemoveDialogOpen(false)
    setFormOpen(false)
  }
  useEffect(() => {
    getLocations()
  }, [getLocations])

  return (
    <>
      <Grid container spacing={2} padding={0}>
        <Grid item xs={12} sm={12} md={7} lg={6}>
          <Stack spacing={2} padding={4}>
            <List>
              {!locations?.length && (
                <ListItem>
                  <Typography variant="body2">
                    {I18n.t('retailerSettings.businessInfo.location.empty')}
                  </Typography>
                  <Button
                    variant="text"
                    onClick={() => {
                      setSelectedLocation(undefined)
                      setFormOpen(true)
                    }}
                  >
                    {I18n.t('retailerSettings.businessInfo.location.add')}
                  </Button>
                </ListItem>
              )}
              {locations?.map((loc) => (
                <ListItem key={loc.id}>
                  <ListItemIcon>
                    <LocationOn />
                  </ListItemIcon>
                  <ListItemText primary={getShortenedAddress(loc)} />
                  <LinkChild
                    to={`/retailer/business-settings/${retailerAccount.slug}/brand-settings`}
                  >
                    <Button
                      sx={{ whiteSpace: 'nowrap' }}
                      variant="text"
                      title={I18n.t(
                        'retailerSettings.businessInfo.location.brandConnectionsDescription',
                      )}
                    >
                      {I18n.t(
                        'retailerSettings.businessInfo.location.brandConnections',
                        { connections: loc.children?.length || 0 },
                      )}
                    </Button>
                  </LinkChild>

                  <IconButton
                    title={I18n.t(
                      'retailerSettings.businessInfo.location.edit',
                    )}
                    aria-label={I18n.t(
                      'retailerSettings.businessInfo.location.edit',
                    )}
                    onClick={() => {
                      setSelectedLocation(loc)
                      setFormOpen(true)
                    }}
                  >
                    <Edit />
                  </IconButton>
                  <IconButton
                    title={I18n.t(
                      'retailerSettings.businessInfo.location.delete',
                    )}
                    aria-label={I18n.t(
                      'retailerSettings.businessInfo.location.delete',
                    )}
                    onClick={() => {
                      setSelectedLocation(loc)
                      setRemoveDialogOpen(true)
                    }}
                  >
                    <Delete />
                  </IconButton>
                </ListItem>
              ))}
            </List>
            <Stack direction="row" justifyContent="flex-end">
              <Button
                startIcon={<Add />}
                onClick={() => {
                  setSelectedLocation(undefined)
                  setFormOpen(true)
                }}
              >
                {I18n.t('retailerSettings.businessInfo.location.add')}
              </Button>
            </Stack>
          </Stack>
        </Grid>
        <Grid
          item
          xs={12}
          sm={12}
          md={5}
          lg={6}
          order={{ xs: -1, sm: -1, md: 'inherit' }}
        >
          <GoogleMap
            style={{ height: '100%', minHeight: 400 }}
            locations={
              locations?.map((location) => ({
                latitude: location.latitude || '',
                longitude: location.longitude || '',
              })) || []
            }
          />
        </Grid>
      </Grid>
      <MobileFullScreenDialog
        fullWidth
        maxWidth="xs"
        open={formOpen}
        onClose={handleClose}
      >
        <DialogContent>
          <BusinessLocationsForm
            onComplete={() => {
              selectedLocation
                ? enqueueSnackbar(
                    I18n.t(
                      'retailerSettings.businessInfo.notifications.updateLocationSuccess',
                    ),
                    { variant: 'success' },
                  )
                : enqueueSnackbar(
                    I18n.t(
                      'retailerSettings.businessInfo.notifications.addLocationSuccess',
                    ),
                    { variant: 'success' },
                  )

              handleClose()
            }}
            onFail={() =>
              enqueueSnackbar(
                I18n.t(
                  'retailerSettings.businessInfo.notifications.locationError',
                ),
                { variant: 'error' },
              )
            }
            onCancel={handleClose}
            location={selectedLocation}
          />
        </DialogContent>
      </MobileFullScreenDialog>
      <ConfirmDialog
        onConfirmClick={async () => {
          try {
            const locationsToDelete =
              selectedLocation?.children?.map((child) => child.id) || []
            if (selectedLocation?.id) {
              locationsToDelete.push(selectedLocation.id)
            }
            const res = await deleteLocations(locationsToDelete)
            if (!res || !res.data || res.errors) {
              throw new Error('location request failed')
            }
            enqueueSnackbar(
              I18n.t(
                'retailerSettings.businessInfo.notifications.deleteLocationSuccess',
              ),
              { variant: 'success' },
            )
          } catch (err) {
            enqueueSnackbar(
              I18n.t(
                'retailerSettings.businessInfo.notifications.deleteLocationError',
              ),
              { variant: 'error' },
            )
          }
          handleClose()
        }}
        open={removeDialogOpen}
        onClose={handleClose}
        title={I18n.t('retailerSettings.businessInfo.location.delete')}
        content={I18n.t('retailerSettings.businessInfo.location.deleteConfirm')}
      />
    </>
  )
}

const BusinessLocationsForm: React.FC<{
  onFail?: () => void
  onComplete?: () => void
  onCancel?: () => void
  location?: Location
}> = ({
  location,
  onCancel = () => {},
  onComplete = () => {},
  onFail = () => {},
}) => {
  const retailerAccount = useSelector(currentRetailerAccountSelector)
  const { createLocation, updateLocations } = useLocations(retailerAccount)
  const mode = location ? 'edit' : 'add'
  return (
    <Formik
      initialValues={{
        addr1: location?.addr1 || '',
        addr2: location?.addr2 || '',
        city: location?.city || '',
        state: location?.state || '',
        country_code: location?.country_code || 'US',
        zip: location?.zip || '',
        phone: location?.phone,
        phone_extension: location?.phone_extension,
        latitude: location?.latitude,
        longitude: location?.longitude,
      }}
      validationSchema={businessLocationSchema}
      onSubmit={async (values, formik) => {
        const payload = {
          ...values,
        } as Location

        try {
          let res
          if (mode === 'edit' && location?.id) {
            const locationsToDelete =
              location?.children?.map((child) => child.id) || []
            if (location?.id) {
              locationsToDelete.push(location.id)
            }
            res = await updateLocations(locationsToDelete, payload)
          } else {
            res = await createLocation(
              retailerAccount.id,
              'retailer_accounts',
              {
                ...payload,
                locationable_type: 'RetailerAccount',
                locationable_id: retailerAccount.id,
                children:
                  retailerAccount.retailers.map((retailer: Retailer) => ({
                    locationable_id: retailer.id,
                    locationable_type: 'Retailer',
                  })) || undefined,
              },
            )
          }
          if (!res || !res.data || res.errors) {
            throw new Error('location request failed')
          }
          onComplete()
        } catch (err) {
          onFail()
        }
        formik.setSubmitting(false)
      }}
    >
      {({ isSubmitting, isValid, setFieldValue, values }) => (
        <Form>
          <Stack spacing={4}>
            <Typography variant="h6" gutterBottom>
              {I18n.t(`retailerSettings.businessInfo.location.${mode}`)}
            </Typography>
            <GooglePlace
              value={values.addr1}
              onInputChange={(value) => {
                setFieldValue('addr1', value)

                setFieldValue('latitude', null)
                setFieldValue('longitude', null)
              }}
              onPlaceSelect={(place) => {
                setFieldValue('addr1', place?.addr1)
                setFieldValue('city', place?.city)
                setFieldValue('state', place?.state)
                setFieldValue('zip', place?.zip)
                setFieldValue('country_code', place?.country)
                setFieldValue('latitude', place?.lat)
                setFieldValue('longitude', place?.long)
              }}
              inputProps={{
                label: I18n.t('retailerSettings.businessInfo.addr1'),
              }}
            />
            <Field
              component={TextField}
              fullWidth
              name="addr2"
              label={I18n.t('retailerSettings.businessInfo.addr2')}
            />

            <Field
              component={TextField}
              fullWidth
              name="city"
              label={I18n.t('retailerSettings.businessInfo.city')}
              required
            />
            <Field
              component={TextField}
              fullWidth
              name="state"
              label={I18n.t('retailerSettings.businessInfo.state')}
              required
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Field
                component={TextField}
                name="country_code"
                type="text"
                label={I18n.t('retailerSettings.businessInfo.country')}
                required
                select
                sx={{ flex: 1 }}
              >
                {acceptedCountries.map((country: AcceptedCountry) => (
                  <MenuItem
                    key={`country-${country.code}}`}
                    value={country.code}
                  >
                    {country.name}
                  </MenuItem>
                ))}
              </Field>
              <Field
                component={TextField}
                name="zip"
                type="text"
                label={I18n.t('retailerSettings.businessInfo.zip')}
                required
                sx={{ flex: 1 }}
              />
            </Stack>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              flexWrap="wrap"
            >
              <Field
                sx={{ flex: 1, minWidth: 150 }}
                component={TextField}
                fullWidth
                name="phone"
                label={I18n.t('retailerSettings.businessInfo.phone')}
              />
              <Field
                sx={{ flex: 0.5, minWidth: 150 }}
                component={TextField}
                fullWidth
                name="phone_extension"
                label={I18n.t('retailerSettings.businessInfo.phoneExtension')}
              />
            </Stack>
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button variant="outlined" onClick={onCancel}>
                {I18n.t('shared.cancel')}
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting || !isValid}
                variant="contained"
                color="primary"
              >
                {I18n.t('shared.save')}
              </Button>
            </Stack>
          </Stack>
        </Form>
      )}
    </Formik>
  )
}

const getShortenedAddress = (location: Location) =>
  `${location.addr1}, ${location.city}, ${location.state}`

export default RetailerSettingsBusinessInfo
